import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { 
  Calendar, Clock, MessageSquare, User, ChevronRight, AlertTriangle, 
  Target, BookOpen, Share2, Download, ArrowLeft, Tag, TrendingUp,
  Twitter, Linkedin, Link as LinkIcon, Lock
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProPaywall from '../components/ProPaywall';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const MacroBriefDetail = () => {
  const { slug } = useParams();
  const { user, isAuthenticated, isPro, login, createAuthHeaders, getToken } = useAuth();
  const [brief, setBrief] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchBrief();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchBrief = async () => {
    setLoading(true);
    try {
      const [briefRes, commentsRes] = await Promise.all([
        axios.get(`${API}/macro-briefs/${slug}`),
        axios.get(`${API}/macro-briefs/${slug}/comments`).catch(() => ({ data: [] }))
      ]);
      setBrief(briefRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error fetching brief:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to comment');
      return;
    }
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/macro-briefs/${brief.brief_id}/comments`,
        { content: newComment },
        { headers: createAuthHeaders(), withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = brief?.title || 'Weekly Macro Brief';
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } else if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/macro-briefs/${brief.slug || brief.brief_id}/pdf`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `DGG-MacroBrief-${brief.slug || 'report'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const formatWeekRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A059]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!brief) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Brief Not Found</h1>
          <Link to="/macro-brief">
            <Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
              <ArrowLeft size={16} className="mr-2" />
              Back to Briefs
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-gray-400">
              <li><Link to="/" className="hover:text-[#C5A059]">Home</Link></li>
              <ChevronRight size={14} />
              <li><Link to="/macro-brief" className="hover:text-[#C5A059]">Macro Brief</Link></li>
              <ChevronRight size={14} />
              <li className="text-white truncate">{brief.title}</li>
            </ol>
          </nav>

          {/* Header */}
          <header className="mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-full px-4 py-2 mb-4">
              <TrendingUp className="text-[#C5A059]" size={16} />
              <span className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider">
                {formatWeekRange(brief.week_start_date, brief.week_end_date)}
              </span>
            </div>

            <h1 className="font-manrope font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight" data-testid="brief-title">
              {brief.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
                  <span className="text-black font-bold text-xs">{brief.author_name?.charAt(0)}</span>
                </div>
                <span>{brief.author_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>{brief.read_time_minutes} min read</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageSquare size={14} />
                <span>{comments.length} comments</span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {brief.tags.map(tag => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/20 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShare('twitter')}
                className="border-white/20 text-gray-300 hover:text-white"
              >
                <Twitter size={14} className="mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShare('linkedin')}
                className="border-white/20 text-gray-300 hover:text-white"
              >
                <Linkedin size={14} className="mr-2" />
                LinkedIn
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShare('copy')}
                className="border-white/20 text-gray-300 hover:text-white"
              >
                <LinkIcon size={14} className="mr-2" />
                Copy Link
              </Button>
              {isPro && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPDF}
                  className="border-[#C5A059]/50 text-[#C5A059] hover:bg-[#C5A059]/10"
                >
                  <Download size={14} className="mr-2" />
                  Download PDF
                </Button>
              )}
            </div>
          </header>

          {/* Pro Gate: Show highlights then paywall for non-pro users */}
          {!isPro ? (
            <div>
              {/* Show highlights as teaser */}
              {brief.highlights && brief.highlights.length > 0 && (
                <div className="bg-gradient-to-br from-[#C5A059]/10 to-transparent border border-[#C5A059]/30 rounded-lg p-6 mb-8">
                  <h2 className="font-manrope font-bold text-lg text-[#C5A059] mb-4 flex items-center gap-2">
                    <BookOpen size={18} />
                    Key Highlights
                  </h2>
                  <ul className="space-y-2">
                    {brief.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-200">
                        <span className="text-[#C5A059] font-bold mt-0.5">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Blurred teaser + Paywall */}
              <div className="relative">
                <div className="opacity-20 pointer-events-none blur-sm">
                  {brief.sections.slice(0, 2).map((section, i) => (
                    <div key={section.id} className="mb-8">
                      <h2 className="font-manrope font-bold text-2xl text-white mb-4">{section.title}</h2>
                      <p className="text-gray-300">Content locked for Pro members...</p>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
              </div>
              <ProPaywall feature="full Macro Brief analysis" />
            </div>
          ) : (
            <>
              {/* Full content for pro users */}

          {/* Highlights Box */}
          {brief.highlights && brief.highlights.length > 0 && (
            <div className="bg-gradient-to-br from-[#C5A059]/10 to-transparent border border-[#C5A059]/30 rounded-lg p-6 mb-8">
              <h2 className="font-manrope font-bold text-lg text-[#C5A059] mb-4 flex items-center gap-2">
                <BookOpen size={18} />
                Key Highlights
              </h2>
              <ul className="space-y-2">
                {brief.highlights.map((highlight, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-200">
                    <span className="text-[#C5A059] font-bold mt-0.5">•</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Risk Callout */}
          {brief.key_risk && (
            <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/30 rounded-lg p-6 mb-8">
              <h2 className="font-manrope font-bold text-lg text-red-400 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} />
                Key Risk This Week
              </h2>
              <p className="text-gray-200">{brief.key_risk}</p>
            </div>
          )}

          {/* Key Levels Box */}
          {brief.key_levels && brief.key_levels.length > 0 && (
            <div className="bg-gradient-to-br from-[#007AFF]/10 to-transparent border border-[#007AFF]/30 rounded-lg p-6 mb-8">
              <h2 className="font-manrope font-bold text-lg text-[#007AFF] mb-4 flex items-center gap-2">
                <Target size={18} />
                Key Levels to Watch
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {brief.key_levels.map((level, i) => (
                  <div key={i} className="bg-black/30 rounded px-4 py-2 text-gray-200 text-sm font-mono">
                    {level}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Table of Contents */}
          {brief.sections && brief.sections.length > 0 && (
            <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-10 sticky top-24 z-10">
              <h3 className="font-manrope font-bold text-sm text-gray-400 uppercase tracking-wider mb-4">
                Table of Contents
              </h3>
              <nav className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {brief.sections.map((section, i) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="text-left text-sm text-gray-300 hover:text-[#C5A059] transition-colors truncate"
                  >
                    <span className="text-[#C5A059] mr-2">{i + 1}.</span>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Sections Content */}
          <div className="prose prose-invert max-w-none">
            {brief.sections.map((section, i) => (
              <section
                key={section.id}
                id={section.id}
                className="mb-12 scroll-mt-32"
              >
                <h2 className="font-manrope font-bold text-2xl text-white mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 bg-[#C5A059] text-black rounded flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </span>
                  {section.title}
                </h2>
                <div className="text-gray-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }}
                />
              </section>
            ))}
          </div>

          {/* Author Box */}
          <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-12">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
                <span className="text-black font-bold text-xl">{brief.author_name?.charAt(0)}</span>
              </div>
              <div>
                <h3 className="font-manrope font-bold text-lg text-white">{brief.author_name}</h3>
                <p className="text-gray-400 text-sm">Research Analyst at Digital Gold Gang</p>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-t border-white/10 pt-12">
            <h2 className="font-manrope font-bold text-2xl text-white mb-8 flex items-center gap-2">
              <MessageSquare size={24} />
              Discussion ({comments.length})
            </h2>

            {/* Comment Form */}
            {isAuthenticated ? (
              <form onSubmit={handleSubmitComment} className="mb-8">
                <Textarea
                  placeholder="Share your thoughts on this week's analysis..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="bg-[#111111] border-white/10 text-white mb-4 min-h-[100px]"
                />
                <Button
                  type="submit"
                  disabled={submitting || !newComment.trim()}
                  className="bg-[#C5A059] text-black hover:bg-[#D4AF37]"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </Button>
              </form>
            ) : (
              <div className="bg-[#111111] border border-white/10 rounded-lg p-6 mb-8 text-center">
                <p className="text-gray-400 mb-4">Join the discussion - login to comment</p>
                <Button onClick={login} className="bg-[#C5A059] text-black hover:bg-[#D4AF37]">
                  Sign in to Comment
                </Button>
              </div>
            )}

            {/* Comments List */}
            {comments.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No comments yet. Be the first to share your thoughts!</p>
            ) : (
              <div className="space-y-6">
                {comments.map(comment => (
                  <div key={comment.comment_id} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#007AFF] to-[#0056b3] flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{comment.author_name?.charAt(0)}</span>
                      </div>
                      <div>
                        <Link
                          to={`/profile/${comment.author_id}`}
                          className="font-medium text-white hover:text-[#C5A059]"
                        >
                          {comment.author_name}
                        </Link>
                        <p className="text-gray-500 text-xs">
                          {new Date(comment.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-300">{comment.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/macro-brief" className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF37]">
              <ArrowLeft size={16} />
              Back to all Macro Briefs
            </Link>
          </div>
          </>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default MacroBriefDetail;
